import {
  createQuery,
  keepPreviousData,
  useQueryClient,
} from "@tanstack/solid-query";
import { createFileRoute, useNavigate } from "@tanstack/solid-router";
import { IconEllipsisVertical, IconPlus } from "~/components/icons";
import JournalEntryTable from "~/components/journal-entry/table/journal-entry-table";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import {
  businessDetailResource,
  businessesResource,
  useActiveBusinessId,
} from "~/features/business/business.resource";
import {
  type JournalEntriesParams,
  JournalEntriesResource,
} from "~/features/journal-entry/journal-entry.resource";
import { withBusinessGuard } from "~/lib/route-guards";

const DEFAULT_LIMIT = 20;

export const Route = createFileRoute("/_dashboard/dashboard/journal-entries/")({
  ...withBusinessGuard(),
  loader: async ({ context }) => {
    const collection = await context.queryClient.ensureQueryData(
      businessesResource.options(undefined),
    );
    const businessId = collection.activeBusinessId!;
    const defaultParams: JournalEntriesParams = {
      businessId,
      limit: DEFAULT_LIMIT,
      sortBy: "date",
      sortDir: "desc",
    };
    const [journalEntries, activeBusiness] = await Promise.all([
      context.queryClient.ensureQueryData(
        JournalEntriesResource.options(defaultParams),
      ),
      context.queryClient.ensureQueryData(
        businessDetailResource.options(businessId),
      ),
    ]);
    return { businessId, journalEntries, activeBusiness };
  },
  component: JournalEntriesPage,
});

function JournalEntriesPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const data = Route.useLoaderData();
  const businessId = useActiveBusinessId();

  businessDetailResource.seedIfEmpty(
    qc,
    data().businessId,
    data().activeBusiness,
  );

  const businessQuery = createQuery(() => ({
    ...businessDetailResource.options(businessId() ?? "", !!businessId()),
    placeholderData: keepPreviousData,
  }));

  const classes = () => businessQuery.data?.classes ?? [];

  const defaultParams: JournalEntriesParams = {
    businessId: data().businessId,
    limit: DEFAULT_LIMIT,
    sortBy: "date",
    sortDir: "desc",
  };
  JournalEntriesResource.seedIfEmpty(qc, defaultParams, data().journalEntries);

  return (
    <div class="flex flex-1 flex-col gap-6 px-4 sm:px-6 pb-3 pt-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold">Journal Entries</h1>
          <p class="text-muted-foreground">Manage your journal entries.</p>
        </div>
        <DropdownMenu placement="bottom-end">
          <DropdownMenuTrigger
            as={Button<"button">}
            variant="outline"
            size="icon"
          >
            <IconEllipsisVertical class="size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent class="w-44">
            <DropdownMenuItem
              onClick={() =>
                navigate({
                  to: "/dashboard/journal-entries/$journalEntryId",
                  params: { journalEntryId: "new" },
                })
              }
            >
              <IconPlus class="size-4" />
              Add Journal Entry
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <JournalEntryTable businessId={businessId() ?? ""} classes={classes()} />
    </div>
  );
}
